import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Typography from 'carbon-react/lib/components/typography';
import { Sidebar } from '@futrli/ui-components';

import { UILoaderSpinner } from '@futrli/ui-components';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { EmailPreview } from '../current-business-insights/email-preview';
import { Recurrence, EmailInsightsSidebarProps } from './types';
import { SendingSelect } from './sending-select';
import { RecurrenceSettings } from '../../../ui/recurrence-settings/recurrence-settings';
import { useEmailInsightsForm } from './use-email-insights-form';

export const NewInsightsEmailSidebar = ({
  view,
  hasSchedules,
  isOpen,
  handleClose,
  period,
  periodData,
  forecastsNotExist,
  selectedForecast,
  performanceToTargetsPeriodData,
  customersSuppliersInfo,
  activeSchedule,
  selectedForecastId
}: EmailInsightsSidebarProps) => {
  const intl = useIntl();
  const [isOpenPreviewEmail, setIsOpenPreviewEmail] = useState(false);

  const form = useEmailInsightsForm({
    activeSchedule,
    selectedForecastId,
    period,
    view,
    handleClose
  });

  const isOptOut = form.recurrence === Recurrence.OPT_OUT_ALL || form.recurrence === Recurrence.OPT_OUT_THIS;
  const isSaveDisabled = form.recurrenceEndDateIsBeforeStartDate || form.recurrenceStartDateError || form.recurrenceRepeatValueError;

  return (
    <Sidebar
      header={
        <Typography variant="h2" fontWeight="700">
          {intl.formatMessage({
            id: 'predict.performance_overview.email_insights',
            defaultMessage: 'Email insights'
          })}
        </Typography>
      }
      onCancel={handleClose}
      open={isOpen}
      size="medium-large"
    >
      {form.isLoading ? (
        <UILoaderSpinner />
      ) : (
        <Form
          stickyFooter
          leftSideButtons={
            <Button
              buttonType="secondary"
              onClick={() => setIsOpenPreviewEmail(true)}
              disabled={isOptOut || isSaveDisabled}
            >
              <FormattedMessage id="reporting.preview_email" defaultMessage="Preview email" />
            </Button>
          }
          saveButton={
            <Button onClick={form.handleSave} disabled={isSaveDisabled} buttonType="primary">
              <FormattedMessage id="common.save" defaultMessage="Save" />
            </Button>
          }
          onSubmit={e => e.preventDefault()}
        >
          <div>
            <WizardRequiredFieldInfo />

            <SendingSelect
              recurrence={form.recurrence}
              onChange={form.handleRecurrenceChange}
              activeSchedule={activeSchedule}
              hasSchedules={hasSchedules}
            />

            {form.showRecurrenceSettings && (
              <RecurrenceSettings
                recurrence={form.recurrence}
                recurrenceStartDate={form.recurrenceStartDate}
                recurrenceStartDateError={form.recurrenceStartDateError}
                recurrenceRepeatValue={form.recurrenceRepeatValue}
                recurrenceRepeatValueError={
                  form.recurrenceRepeatValueError
                    ? intl.formatMessage({ id: 'predict.schedule_every_required', defaultMessage: 'Repeat interval must be at least 1' })
                    : undefined
                }
                recurrenceEndType={form.recurrenceEndType}
                occurancesValue={form.occurancesValue}
                recurrenceEndDate={form.recurrenceEndDate}
                recurrenceEndDateIsBeforeStartDate={form.recurrenceEndDateIsBeforeStartDate}
                recurrenceMonthDay={form.recurrenceMonthDay}
                recurrenceWeekDay={form.recurrenceWeekDay}
                onStartDateChange={form.handleRecurrenceStartDateChange}
                onRepeatValueChange={form.handleRecurrenceValueChange}
                onEndTypeChange={form.handleRecurrenceEndTypeChange}
                onOccurancesValueChange={form.handleOccurancesValueChange}
                onEndDateChange={form.handleRecurrenceEndDateChange}
                onWeekDayChange={form.handleRecurrenceWeekDayChange}
                onMonthDayChange={form.handleRecurrenceMonthDay}
              />
            )}

            {isOpenPreviewEmail && (
              <EmailPreview
                period={period}
                isOpen={isOpenPreviewEmail}
                handleClose={() => setIsOpenPreviewEmail(false)}
                periodData={periodData}
                forecastsNotExist={forecastsNotExist}
                performanceToTargetsPeriodData={performanceToTargetsPeriodData}
                selectedForecast={selectedForecast}
                customersSuppliersInfo={customersSuppliersInfo}
              />
            )}
          </div>
        </Form>
      )}
    </Sidebar>
  );
};
